import { Injectable, inject } from '@angular/core';

import { createSelector, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import type { AbilitiesMap } from '@core/abilities-data';
import { AbilitiesSelectors } from '@core/abilities-data';
import { CurrentUserSelectors, UserSelectors } from '@core/account-data';
import type { CurrentUser, User } from '@core/account-types';
import { filterBoolean } from '@core/common-legacy';
import { memoizeSelector } from '@core/ngrx-legacy';
import { NoteSelectors } from '@core/notes-data';
import type { Note } from '@core/notes-types';
import type { Requisition } from '@core/requisitions-types';

import { RootPageDataService } from './root-page-data.service';

const isLoaded = memoizeSelector((requisitionId: number) =>
  createSelector(
    NoteSelectors.requisitionScopeIsLoaded(requisitionId),
    UserSelectors.isLoaded(),
    CurrentUserSelectors.isLoaded,
    (a, b, c) => a && b && c,
  ),
);

@Injectable()
export class NotesPageDataService {
  private readonly rootData = inject(RootPageDataService);
  private readonly store = inject(Store);

  public requisition$: Observable<Requisition>;
  public notes$: Observable<Note[]>;
  public users$: Observable<User[]>;
  public currentUser$: Observable<CurrentUser>;
  public abilities$: Observable<AbilitiesMap>;
  public isLoaded$: Observable<boolean>;

  constructor() {
    const rootData = this.rootData;
    const store = this.store;

    this.requisition$ = rootData.requisition$;
    const requisitionId$ = this.requisition$.pipe(map(requisition => requisition.id));
    this.notes$ = requisitionId$.pipe(
      switchMap(id => store.select(NoteSelectors.requisitionScopeNested(id)).pipe(filterBoolean)),
    );
    this.users$ = store.select(UserSelectors.entities()).pipe(filterBoolean);
    this.currentUser$ = store.select(CurrentUserSelectors.data).pipe(filterBoolean);
    this.abilities$ = store.select(AbilitiesSelectors.abilityMap).pipe(filterBoolean);
    this.isLoaded$ = requisitionId$.pipe(switchMap(id => store.select(isLoaded(id))));
  }
}
